import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { PageLayout } from '@/components/shared/PageLayout';
import { Button } from '@/components/ui/button';
import { RefreshCcw, ScanLine } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ToolListFilters } from '../components/ToolListFilters';
import { ToolsList } from '../components/ToolsList';
import { PageHeader } from '@/components/shared/PageHeader';
import { toolState } from '@/features/traceability/data/toolState';
import { ScanPopUp } from '../components/ScanPopUp';
import { AddToolDialog } from '../components/AddToolDialog';
import { useToolsFetch } from '../hooks/useToolsFetch';
import { useToolListFilters } from '../hooks/useToolListFilters';
import { useReferenceExpansion } from '../hooks/useReferenceExpansion';

const buildToolDetailPath = (toolId: string) => `/traceability/tools/${encodeURIComponent(toolId)}`;

export function ToolListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { isLoading, apiResult, hierarchy, handleRetry } = useToolsFetch();

  const {
    search,
    referenceFilter,
    healthFilter,
    filteredHierarchy,
    hasMoreResults,
    handleSearchChange,
    handleReferenceChange,
    handleHealthChange,
    handleResetFilters,
    handleShowMore,
  } = useToolListFilters(hierarchy);

  // Reference identifiers (all unique references)
  const referenceIdentifiers = useMemo(
    () => Array.from(new Set(hierarchy.map((ref) => ref.reference))),
    [hierarchy]
  );

  // Get reference identifiers from filtered hierarchy (not full hierarchy)
  const filteredReferenceIdentifiers = useMemo(
    () => filteredHierarchy.map((ref) => ref.reference),
    [filteredHierarchy]
  );

  const { expandedReferences, expandAll, collapseAll, toggleReference, allExpanded, anyExpanded } =
    useReferenceExpansion(referenceIdentifiers, filteredReferenceIdentifiers);

  // Scan dialog state
  const [isScanDialogOpen, setIsScanDialogOpen] = useState(false);

  // Unknown tool dialog state (toolId not found in hierarchy)
  const [scannedUnknownToolId, setScannedUnknownToolId] = useState<string | null>(null);

  // Get all health options from toolState
  const healthOptions = toolState.map((opt) => ({ value: opt.value, labelKey: opt.labelKey }));

  const navigateToToolDetail = useCallback(
    (toolId: string) => {
      if (!toolId) {
        return;
      }

      navigate(buildToolDetailPath(toolId));
    },
    [navigate]
  );

  // Scan dialog handlers
  const handleOpenScanDialog = useCallback(() => {
    setIsScanDialogOpen(true);
  }, []);

  const handleCloseScanDialog = useCallback(() => {
    setIsScanDialogOpen(false);
  }, []);

  const handleToolScanned = useCallback(
    (toolId: string) => {
      // Check against full (unfiltered) hierarchy
      const exists = hierarchy.some((ref) => ref.tools.some((t) => t.id === toolId));
      if (exists) {
        navigateToToolDetail(toolId);
      } else {
        setIsScanDialogOpen(false);
        setScannedUnknownToolId(toolId);
      }
    },
    [hierarchy, navigateToToolDetail]
  );

  // Table rows show tools from expanded references only (mirrors hierarchy view)
  // Sort by most recent last trace date
  const tableRows = useMemo(() => {
    const rows = filteredHierarchy
      .filter((reference) => expandedReferences.has(reference.reference))
      .flatMap((reference) =>
        reference.tools.map((tool) => ({
          id: tool.id,
          reference: reference.reference,
          family: reference.family,
          health: reference.health,
          toolState: tool.toolState,
          lastTraceDate: tool.lastTraceDate,
          traceCount: tool.traceCount,
          onNavigate: () => navigateToToolDetail(tool.id),
        }))
      );

    // Sort by lastTraceDate, most recent first
    return rows.sort((a, b) => {
      const aDate = a.lastTraceDate === null ? 0 : new Date(a.lastTraceDate).getTime();
      const bDate = b.lastTraceDate === null ? 0 : new Date(b.lastTraceDate).getTime();
      return bDate - aDate;
    });
  }, [navigateToToolDetail, filteredHierarchy, expandedReferences]);

  const breadcrumbItems = [
    {
      label: t('common.brand'),
      onClick: () => navigate('/'),
    },
    {
      label: t('toolView.list.breadcrumb.tools'),
    },
  ];

  const totalTools = hierarchy.reduce((sum, r) => sum + r.tools.length, 0);

  return (
    <>
      <PageLayout>
        <div className="flex flex-col gap-3">
          <PageHeader
            className="px-5 py-3"
            breadcrumb={breadcrumbItems}
            title={
              <>
                {t('toolView.list.pageTitle')}
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  · {totalTools} {t('toolView.list.breadcrumb.tools').toLowerCase()}
                </span>
              </>
            }
            actions={
              <>
                <Button variant="outline" className="gap-2" onClick={handleOpenScanDialog}>
                  <ScanLine className="size-4" aria-hidden />
                  {t('toolView.list.actions.scan')}
                </Button>
                <Button
                  variant="default"
                  className="gap-2"
                  onClick={handleRetry}
                  loading={isLoading}
                >
                  {!isLoading && <RefreshCcw className="size-4" aria-hidden />}
                  {t('toolView.list.actions.refresh')}
                </Button>
              </>
            }
          />

          <section className="flex flex-col gap-3">
            <ToolListFilters
              search={search}
              references={referenceFilter}
              allReferences={referenceIdentifiers}
              health={healthFilter}
              healthOptions={healthOptions}
              onSearchChange={handleSearchChange}
              onReferenceChange={handleReferenceChange}
              onHealthChange={handleHealthChange}
              onReset={handleResetFilters}
            />

            <ToolsList
              isLoading={isLoading}
              apiResult={apiResult}
              hierarchy={filteredHierarchy}
              tableRows={tableRows}
              totalReferences={filteredHierarchy.length}
              expandedReferences={expandedReferences}
              hasMoreResults={hasMoreResults}
              onShowMore={handleShowMore}
              allExpanded={allExpanded}
              anyExpanded={anyExpanded}
              onToggleReference={toggleReference}
              onNavigateToTool={navigateToToolDetail}
              onRetry={handleRetry}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
            />
          </section>
        </div>
      </PageLayout>

      {/* Scan Dialog */}
      <ScanPopUp
        open={isScanDialogOpen}
        onClose={handleCloseScanDialog}
        onDetected={handleToolScanned}
      />

      {/* Add Tool Dialog — shown when scanned ID is not in the database */}
      <AddToolDialog
        open={!!scannedUnknownToolId}
        toolId={scannedUnknownToolId ?? ''}
        onClose={() => setScannedUnknownToolId(null)}
        onSuccess={(toolId) => {
          setScannedUnknownToolId(null);
          navigateToToolDetail(toolId);
        }}
      />
    </>
  );
}
